// BusinessCard — one component that renders any of the 10 designs.
// Each variant has full + mini (thumb) versions, switchable via `size` prop.
// All styles live in src/styles/cards.css (imported once from main.css).
import type { ReactElement, ReactNode } from "react";

export type CardStyle =
  | "editorial"
  | "modernist"
  | "ink"
  | "polaroid"
  | "brutalist"
  | "terminal"
  | "organic"
  | "ticket"
  | "magazine"
  | "botanical";

// eslint-disable-next-line react-refresh/only-export-components
export const CARD_STYLES: { value: CardStyle; label: string; tag: string; blurb: string }[] = [
  { value: "editorial",  label: "Editorial Paper",  tag: "Literary",     blurb: "Cream paper, slightly tilted, mono contact rows." },
  { value: "modernist",  label: "Modernist Sans",   tag: "Corporate",    blurb: "White, sharp corners, square photo, clean stack." },
  { value: "ink",        label: "Ink & Foil",       tag: "Luxury",       blurb: "Dark, gold-gradient name, thin gold inner border." },
  { value: "polaroid",   label: "Polaroid",         tag: "Warm",         blurb: "Square photo top, italic display name, tilted." },
  { value: "brutalist",  label: "Brutalist Grid",   tag: "Designer",     blurb: "Four ink quadrants, terra accent band, solid shadow." },
  { value: "terminal",   label: "Terminal",         tag: "Dev",          blurb: "CLI window, mono green-on-black, blinking cursor." },
  { value: "organic",    label: "Organic Pastel",   tag: "Wellness",     blurb: "Soft sandstone gradient, rounded, contact pills." },
  { value: "ticket",     label: "Ticket Stub",      tag: "Event",        blurb: "Perforated divider, admission number, barcode strip." },
  { value: "magazine",   label: "Magazine Cover",   tag: "Publisher",    blurb: "Full-bleed photo + masthead, terra accent rule." },
  { value: "botanical",  label: "Botanical",        tag: "Academic",     blurb: "Herbarium sheet, brass corners, italic Latinate name." },
];

export interface CardUser {
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  designation?: string | null;
  email?: string | null;
  phone?: string | null;
  web?: string | null;
  address?: string | null;
  profile?: string | null;     // image URL
}

interface BusinessCardProps {
  variant: CardStyle | string | null | undefined;
  user: CardUser;
  size?: "full" | "thumb";
}

// ──────── Helpers ────────

function displayName(u: CardUser): string {
  const full = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return full || u.username;
}

function initials(u: CardUser): string {
  const f = (u.first_name ?? "").trim()[0];
  const l = (u.last_name ?? "").trim()[0];
  if (f || l) return `${f ?? ""}${l ?? ""}`.toUpperCase();
  return u.username.slice(0, 2).toUpperCase();
}

function trimUrl(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function safeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const t = url.trim();
  if (!t) return null;
  if (/^[a-zA-Z][\w+.-]*:/.test(t)) return t;
  return `https://${t}`;
}

// Photo helper — image when available, initials block otherwise.
// Renders inside an existing styled box (parent supplies size + background).
function PhotoOrInitials({ u, className }: { u: CardUser; className: string }) {
  if (u.profile) {
    // Inline cover/center: the per-variant CSS uses `background: linear-gradient(...)`
    // shorthand for the initials-fallback color, which resets background-size
    // and background-position to their defaults (`auto` / `0% 0%`). Without
    // these inline values the photo would render in the top-left corner of the
    // avatar at original pixel size. Inline wins via higher specificity.
    return (
      <div
        className={className}
        style={{
          backgroundImage: `url(${u.profile})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        aria-hidden="true"
      />
    );
  }
  return <div className={className}>{initials(u)}</div>;
}

const A = ({ href, children }: { href: string | null; children: ReactNode }) =>
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <>{children}</>
  );

// Normalize unknown / legacy values to a known variant.
function normalize(v: string | null | undefined): CardStyle {
  if (!v) return "editorial";
  const known = CARD_STYLES.map((s) => s.value);
  return (known as string[]).includes(v) ? (v as CardStyle) : "editorial";
}

// ──────── Dispatcher ────────

export function BusinessCard({ variant, user, size = "full" }: BusinessCardProps) {
  const v = normalize(variant);
  const Component = size === "thumb" ? THUMB_VARIANTS[v] : FULL_VARIANTS[v];
  return <Component user={user} />;
}

// ──────── 01 · Editorial Paper ────────

function EditorialFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-editorial">
      <PhotoOrInitials u={user} className="bc-photo" />
      <div className="bc-name">{displayName(user)}</div>
      {user.designation && <div className="bc-role">{user.designation}</div>}
      <hr />
      <dl>
        {user.email && (
          <>
            <dt>Email</dt>
            <dd>
              <A href={`mailto:${user.email}`}>{user.email}</A>
            </dd>
          </>
        )}
        {user.phone && (
          <>
            <dt>Phone</dt>
            <dd>
              <A href={`tel:${user.phone}`}>{user.phone}</A>
            </dd>
          </>
        )}
        {user.address && (
          <>
            <dt>Studio</dt>
            <dd>{user.address}</dd>
          </>
        )}
        {safeUrl(user.web) && (
          <>
            <dt>Web</dt>
            <dd>
              <A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A>
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}

function EditorialThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-editorial">
      <PhotoOrInitials u={user} className="bcm-photo" />
      <div className="bcm-name">{displayName(user)}</div>
      {user.designation && <div className="bcm-role">{user.designation}</div>}
      <hr />
      <div className="bcm-rows">
        {user.email && (<><div className="k">Email</div><div>{user.email}</div></>)}
        {user.phone && (<><div className="k">Phone</div><div>{user.phone}</div></>)}
        {user.address && (<><div className="k">Studio</div><div>{user.address}</div></>)}
      </div>
    </div>
  );
}

// ──────── 02 · Modernist Sans ────────

function ModernistFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-modernist">
      <div className="bc-top">
        <PhotoOrInitials u={user} className="bc-photo" />
        <div className="bc-who">
          {user.designation && <div className="bc-role">{user.designation}</div>}
          <div className="bc-name">{displayName(user)}</div>
        </div>
      </div>
      <div className="bc-stack">
        {user.email && (
          <div className="row"><span className="icon">@</span><A href={`mailto:${user.email}`}>{user.email}</A></div>
        )}
        {user.phone && (
          <div className="row"><span className="icon">☎</span><A href={`tel:${user.phone}`}>{user.phone}</A></div>
        )}
        {user.address && (
          <div className="row"><span className="icon">⌂</span>{user.address}</div>
        )}
        {safeUrl(user.web) && (
          <div className="row"><span className="icon">↗</span><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></div>
        )}
      </div>
    </div>
  );
}

function ModernistThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-modernist">
      <div className="bcm-top">
        <PhotoOrInitials u={user} className="bcm-photo" />
        <div>
          {user.designation && <div className="bcm-role">{user.designation}</div>}
          <div className="bcm-name">{displayName(user)}</div>
        </div>
      </div>
      <div className="bcm-stack">
        {user.email && <div className="row"><span className="icon">@</span>{user.email}</div>}
        {user.phone && <div className="row"><span className="icon">☎</span>{user.phone}</div>}
        {user.address && <div className="row"><span className="icon">⌂</span>{user.address}</div>}
      </div>
    </div>
  );
}

// ──────── 03 · Ink & Foil ────────

function InkFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-ink">
      <PhotoOrInitials u={user} className="bc-photo" />
      <div className="bc-name">{displayName(user)}</div>
      {user.designation && <div className="bc-role">{user.designation}</div>}
      <hr />
      <dl>
        {user.email && (<><dt>Email</dt><dd><A href={`mailto:${user.email}`}>{user.email}</A></dd></>)}
        {user.phone && (<><dt>Phone</dt><dd><A href={`tel:${user.phone}`}>{user.phone}</A></dd></>)}
        {user.address && (<><dt>Studio</dt><dd>{user.address}</dd></>)}
        {safeUrl(user.web) && (<><dt>Web</dt><dd><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></dd></>)}
      </dl>
    </div>
  );
}

function InkThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-ink">
      <PhotoOrInitials u={user} className="bcm-photo" />
      <div className="bcm-name">{displayName(user)}</div>
      {user.designation && <div className="bcm-role">{user.designation}</div>}
      <hr />
      <div className="bcm-rows">
        {user.email && (<><div className="k">Email</div><div>{user.email}</div></>)}
        {user.phone && (<><div className="k">Phone</div><div>{user.phone}</div></>)}
        {user.address && (<><div className="k">Studio</div><div>{user.address}</div></>)}
      </div>
    </div>
  );
}

// ──────── 04 · Polaroid ────────

function PolaroidFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-polaroid">
      <PhotoOrInitials u={user} className="bc-photo" />
      <div className="bc-name">{displayName(user)}</div>
      {user.designation && <div className="bc-role">{user.designation}</div>}
      <div className="bc-meta">
        {user.email && <div><A href={`mailto:${user.email}`}>{user.email}</A></div>}
        {(user.phone || user.address) && (
          <div>
            {user.phone && <><A href={`tel:${user.phone}`}>{user.phone}</A>{user.address && " · "}</>}
            {user.address}
          </div>
        )}
        {safeUrl(user.web) && <div><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></div>}
      </div>
    </div>
  );
}

function PolaroidThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-polaroid">
      <PhotoOrInitials u={user} className="bcm-photo" />
      <div className="bcm-name">{displayName(user)}</div>
      {user.designation && <div className="bcm-role">{user.designation}</div>}
      <div className="bcm-meta">
        {user.email}
        {user.phone && <><br/>{user.phone}</>}
      </div>
    </div>
  );
}

// ──────── 05 · Brutalist Grid ────────

function BrutalistFull({ user }: { user: CardUser }) {
  const [first, ...rest] = displayName(user).split(" ");
  const last = rest.join(" ");
  return (
    <div className="bc-brutalist">
      <div className="bc-grid">
        <PhotoOrInitials u={user} className="bc-photo" />
        <div className="bc-who">
          <div className="bc-name">{first}{last && <><br/>{last}</>}</div>
        </div>
      </div>
      {user.designation && <div className="bc-role-row">{user.designation}</div>}
      <div className="bc-meta">
        {user.email && (
          <div className="row"><strong>EMAIL</strong><A href={`mailto:${user.email}`}>{user.email}</A></div>
        )}
        {user.phone && (
          <div className="row"><strong>PHONE</strong><A href={`tel:${user.phone}`}>{user.phone}</A></div>
        )}
        {user.address && <div className="row"><strong>STUDIO</strong>{user.address}</div>}
        {safeUrl(user.web) && (
          <div className="row"><strong>WEB</strong><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></div>
        )}
      </div>
    </div>
  );
}

function BrutalistThumb({ user }: { user: CardUser }) {
  const [first, ...rest] = displayName(user).split(" ");
  const last = rest.join(" ");
  return (
    <div className="bcm-brutalist">
      <div className="bcm-grid">
        <PhotoOrInitials u={user} className="bcm-photo" />
        <div className="bcm-who">{first}{last && <><br/>{last}</>}</div>
        {user.designation && <div className="bcm-role-band">{user.designation}</div>}
        <div className="bcm-rows">
          {user.email && <div className="row"><strong>EMAIL</strong>{user.email}</div>}
          {user.phone && <div className="row"><strong>PHONE</strong>{user.phone}</div>}
          {user.address && <div className="row"><strong>STUDIO</strong>{user.address}</div>}
        </div>
      </div>
    </div>
  );
}

// ──────── 06 · Terminal ────────

function TerminalFull({ user }: { user: CardUser }) {
  const handle = user.username;
  return (
    <div className="bc-terminal">
      <div className="bc-winbar">
        <span className="dot" /><span className="dot" /><span className="dot" />
        <span className="title">~ / {handle} — varo.biz</span>
      </div>
      <div><span className="p">{handle}@varo</span> <span className="c">~$ whoami</span></div>
      <div className="o">{displayName(user)}{user.designation ? ` · ${user.designation}` : ""}</div>
      <div style={{ marginTop: 8 }}><span className="p">{handle}@varo</span> <span className="c">~$ cat avatar</span></div>
      <div className="o">{user.profile ? (
        <img src={user.profile} alt="" style={{ width: 56, height: 56, borderRadius: 4, objectFit: "cover" }} />
      ) : (
        <span className="photo-block">{initials(user)}</span>
      )}</div>
      <div><span className="p">{handle}@varo</span> <span className="c">~$ contact --all</span></div>
      {user.email && <div className="o"><span className="k">email </span>{user.email}</div>}
      {user.phone && <div className="o"><span className="k">phone </span>{user.phone}</div>}
      {user.address && <div className="o"><span className="k">studio</span>{user.address}</div>}
      {safeUrl(user.web) && <div className="o"><span className="k">web   </span><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></div>}
      <div style={{ marginTop: 8 }}><span className="p">{handle}@varo</span> <span className="c">~$</span> <span className="blink">▍</span></div>
    </div>
  );
}

function TerminalThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-terminal">
      <div className="bcm-winbar">
        <span className="dot" /><span className="dot" /><span className="dot" />
      </div>
      <div><span className="p">{user.username}@varo</span> ~$ whoami</div>
      <div className="o">{displayName(user)}{user.designation ? ` · ${user.designation}` : ""}</div>
      <div><span className="p">{user.username}@varo</span> ~$ avatar</div>
      <div className="o"><span className="photo-block">{initials(user)}</span></div>
      <div><span className="p">{user.username}@varo</span> ~$ contact</div>
      {user.email && <div className="o"><span className="k">email</span> {user.email}</div>}
      {user.phone && <div className="o"><span className="k">phone</span> {user.phone}</div>}
    </div>
  );
}

// ──────── 07 · Organic Pastel ────────

function OrganicFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-organic">
      <PhotoOrInitials u={user} className="bc-photo" />
      <div className="bc-name">{displayName(user)}</div>
      {user.designation && <div className="bc-role">{user.designation}</div>}
      <div className="bc-pills">
        {user.email && <span className="bc-pill"><strong>✉</strong> <A href={`mailto:${user.email}`}>{user.email}</A></span>}
        {user.phone && <span className="bc-pill"><strong>☎</strong> <A href={`tel:${user.phone}`}>{user.phone}</A></span>}
        {user.address && <span className="bc-pill"><strong>⌂</strong> {user.address}</span>}
        {safeUrl(user.web) && <span className="bc-pill"><strong>↗</strong> <A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></span>}
      </div>
    </div>
  );
}

function OrganicThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-organic">
      <PhotoOrInitials u={user} className="bcm-photo" />
      <div className="bcm-name">{displayName(user)}</div>
      {user.designation && <div className="bcm-role">{user.designation}</div>}
      <div className="bcm-pills">
        {user.email && <span className="bcm-pill">✉ {user.email}</span>}
        {user.phone && <span className="bcm-pill">☎ {user.phone}</span>}
        {user.address && <span className="bcm-pill">⌂ {user.address}</span>}
      </div>
    </div>
  );
}

// ──────── 08 · Ticket Stub ────────

function ticketNumber(user: CardUser): string {
  return String(user.username.length * 7 + 42).padStart(3, "0").slice(-3);
}

function TicketFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-ticket">
      <div className="bc-stub-body">
        <div className="bc-top-line">
          <span>Admit one</span>
          <span>{new Date().getFullYear()} · {String(new Date().getMonth() + 1).padStart(2, "0")}</span>
        </div>
        <div className="bc-name">{displayName(user)}</div>
        {user.designation && <div className="bc-role">{user.designation}</div>}
        <dl>
          {user.email && (<><dt>Email</dt><dd><A href={`mailto:${user.email}`}>{user.email}</A></dd></>)}
          {user.phone && (<><dt>Phone</dt><dd><A href={`tel:${user.phone}`}>{user.phone}</A></dd></>)}
          {user.address && (<><dt>Studio</dt><dd>{user.address}</dd></>)}
          {safeUrl(user.web) && (<><dt>Web</dt><dd><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></dd></>)}
        </dl>
      </div>
      <div className="bc-stub-side">
        <div>
          <div className="label">No.</div>
          <div className="num">{ticketNumber(user)}</div>
        </div>
        <div>
          <div className="barcode" />
          <div className="id">{initials(user)}·{ticketNumber(user)}</div>
        </div>
      </div>
    </div>
  );
}

function TicketThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-ticket">
      <div className="bcm-stub">
        <div className="bcm-top-line">
          <span>Admit one</span><span>{new Date().getFullYear()}</span>
        </div>
        <div className="bcm-name">{displayName(user)}</div>
        {user.designation && <div className="bcm-role">{user.designation}</div>}
        <div className="bcm-rows">
          {user.email && <><span className="k">@</span> {user.email}<br/></>}
          {user.phone && <><span className="k">☎</span> {user.phone}<br/></>}
          {user.address && <><span className="k">⌂</span> {user.address}</>}
        </div>
      </div>
      <div className="bcm-side">
        <div className="num">{ticketNumber(user)}</div>
        <div className="bc" />
      </div>
    </div>
  );
}

// ──────── 09 · Magazine Cover ────────

function MagazineFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-magazine">
      <div className="bc-masthead">
        <div className="title">Varo</div>
        <div className="issue">Vol. 1 · No. {ticketNumber(user)}</div>
      </div>
      <div
        className="bc-cover-photo"
        style={user.profile ? { backgroundImage: `url(${user.profile})` } : undefined}
      >
        {!user.profile && <span>{initials(user)}</span>}
        <div className="bc-cover-text">
          {user.designation && <div className="role">{user.designation}</div>}
          <div className="name">
            {displayName(user).split(" ").map((part, i, a) => (
              <span key={i}>{part}{i < a.length - 1 && <br />}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="bc-strip">
        {user.email && <div><span className="k">EMAIL</span> <A href={`mailto:${user.email}`}>{user.email}</A></div>}
        {user.phone && <div><span className="k">PHONE</span> <A href={`tel:${user.phone}`}>{user.phone}</A></div>}
        {user.address && <div><span className="k">STUDIO</span> {user.address}</div>}
        {safeUrl(user.web) && <div><span className="k">WEB</span> <A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></div>}
      </div>
    </div>
  );
}

function MagazineThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-magazine">
      <div className="bcm-mh">
        <div className="t">Varo</div>
        <div className="iss">No. {ticketNumber(user)}</div>
      </div>
      <div
        className="bcm-photo"
        style={user.profile ? { backgroundImage: `url(${user.profile})` } : undefined}
      >
        {!user.profile && <span>{initials(user)}</span>}
        <div className="bcm-overlay">
          {user.designation && <div className="bcm-role">{user.designation}</div>}
          <div className="bcm-name">{displayName(user)}</div>
        </div>
      </div>
      <div className="bcm-strip">
        {user.email && <div><span className="k">@</span> {user.email}</div>}
        {user.phone && <div><span className="k">☎</span> {user.phone}</div>}
      </div>
    </div>
  );
}

// ──────── 10 · Botanical Specimen ────────

function BotanicalFull({ user }: { user: CardUser }) {
  return (
    <div className="bc-botanical">
      <div className="bc-header">Specimen · No. {ticketNumber(user)}</div>
      <PhotoOrInitials u={user} className="bc-photo" />
      <div className="bc-name">{displayName(user)}</div>
      {user.designation && <div className="bc-role">{user.designation}</div>}
      <div className="bc-divider">— · field notes · —</div>
      <dl>
        {user.email && (<><dt>Email</dt><dd><A href={`mailto:${user.email}`}>{user.email}</A></dd></>)}
        {user.phone && (<><dt>Phone</dt><dd><A href={`tel:${user.phone}`}>{user.phone}</A></dd></>)}
        {user.address && (<><dt>Habitat</dt><dd>{user.address}</dd></>)}
        {safeUrl(user.web) && (<><dt>Web</dt><dd><A href={safeUrl(user.web)}>{trimUrl(user.web!)}</A></dd></>)}
      </dl>
    </div>
  );
}

function BotanicalThumb({ user }: { user: CardUser }) {
  return (
    <div className="bcm-botanical">
      <div className="bcm-header">Specimen · {ticketNumber(user)}</div>
      <PhotoOrInitials u={user} className="bcm-photo" />
      <div className="bcm-name">{displayName(user)}</div>
      {user.designation && <div className="bcm-role">{user.designation}</div>}
      <div className="bcm-data">
        {user.email && (<><div className="k">Email</div><div>{user.email}</div></>)}
        {user.phone && (<><div className="k">Phone</div><div>{user.phone}</div></>)}
        {user.address && (<><div className="k">Habitat</div><div>{user.address}</div></>)}
      </div>
    </div>
  );
}

// ──────── Variant maps ────────

const FULL_VARIANTS: Record<CardStyle, (p: { user: CardUser }) => ReactElement> = {
  editorial: EditorialFull,
  modernist: ModernistFull,
  ink:       InkFull,
  polaroid:  PolaroidFull,
  brutalist: BrutalistFull,
  terminal:  TerminalFull,
  organic:   OrganicFull,
  ticket:    TicketFull,
  magazine:  MagazineFull,
  botanical: BotanicalFull,
};

const THUMB_VARIANTS: Record<CardStyle, (p: { user: CardUser }) => ReactElement> = {
  editorial: EditorialThumb,
  modernist: ModernistThumb,
  ink:       InkThumb,
  polaroid:  PolaroidThumb,
  brutalist: BrutalistThumb,
  terminal:  TerminalThumb,
  organic:   OrganicThumb,
  ticket:    TicketThumb,
  magazine:  MagazineThumb,
  botanical: BotanicalThumb,
};
